import { lazy } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import { Box } from '@mui/material';
import {
  ProtectedRoute, AdminRoute, NoLoginRoute, Route, ProfileRoute,
} from '@client/components/common/Route';
import { MainNav } from '@client/components/Main/MainNav';
import { Modal } from '@client/components/globals/Modal';
import { DocumentTitle } from '@client/components/common/DocumentTitle';
import { Snackbar } from '@client/components/globals/Snackbar';
import { useSelector } from '@client/hooks/redux.hooks';
import { isFullScreenSelector } from '@client/selectors/helpers/router';
import { BottomAppBar } from '@client/components/Main/MainNav/Mobile/BottomAppBar';
import { MobileSpacer } from '@client/components/Main/MainNav/Mobile/MobileSpacer';
import { Footer } from '@client/components/Main/Footer';
import { CookieBanner } from '@client/components/Main/CookieBanner';
import { EventWatcherContainer } from '../containers/_helpers/EventWatcherContainer';
import { Analytics } from './_helpers/Analytics';
import Main from './Main';

const HomeLoader = lazy(() => import(/* webpackPrefetch: true */ '@client/components/routes/Home'));
const ForArtistsAndHostsLoader = lazy(() => import('@client/components/routes/ForArtistsAndHosts'));
const AboutUsLoader = lazy(() => import('@client/components/routes/AboutUs'));
const ConnectLoader = lazy(() => import('./Connect'));
const MessagesLoader = lazy(() => import('./Messages'));
const MyTicketsLoader = lazy(() => import('./MyTickets'));
const MyShowsLoader = lazy(() => import('./MyShows'));
const ShowCallsLoader = lazy(() => import('./ShowCalls'));
const SettingsLoader = lazy(() => import('./Settings'));
const DashboardLoader = lazy(() => import('./Dashboard'));
const ReviewsLoader = lazy(() => import('./Reviews'));
const LoginLoader = lazy(() => import('@client/components/routes/Login'));
const ForgotPasswordLoader = lazy(() => import('./ForgotPassword'));
const CheckoutLoader = lazy(() => import('./Checkout'));
const ShowsLoader = lazy(() => import('./Shows'));
const ArtistsLoader = lazy(() => import('./Artists'));
const HostsLoader = lazy(() => import('./Venues'));
const ContactUsLoader = lazy(() => import('@client/components/routes/ContactUs'));
const ConsultationLoader = lazy(() => import('./Consultation'));
const AdminLoader = lazy(() => import('./Admin'));
const ProfileLoader = lazy(() => import('./Profile'));
const TicketsLoader = lazy(() => import('./Tickets'));
const InviteLoader = lazy(() => import('@client/components/routes/Invite'));
const SignupLoader = lazy(() => import('./Signup'));
const TermsOfServiceLoader = lazy(() => import('./Legal/Terms'));
const PrivacyPolicyLoader = lazy(() => import('./Legal/Privacy'));
const FourOhFourLoader = lazy(() => import('@client/components/Main/FourOhFour'));
const OffersLoader = lazy(() => import('./Offers'));

export default () => {
  const isFullScreen = useSelector(isFullScreenSelector);

  return (
    <>
      <Box
        className="fill-available"
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          minHeight: {
            md: '100vh',
          },
        }}
      >
        <DocumentTitle />

        <MainNav />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            p: {
              xs: 0,
              md: isFullScreen ? 0 : 3,
            },
            pt: 0,
            minHeight: {
              md: '100vh',
            },
          }}
        >
          <Switch>
            <Route
              path="/"
              exact
              component={Main}
            />

            <Route
              path="/home"
              exact
              load={HomeLoader}
            />

            <Route
              path={['/for-artists-and-hosts', '/for-hosts']}
              exact
              load={ForArtistsAndHostsLoader}
            />

            <Route
              path="/about-us"
              exact
              load={AboutUsLoader}
            />

            <Route
              path="/connect"
              load={ConnectLoader}
            />

            <ProtectedRoute
              path="/messages"
              load={MessagesLoader}
            />

            <ProtectedRoute
              path="/mytickets"
              load={MyTicketsLoader}
            />

            <ProtectedRoute
              path="/myshows"
              load={MyShowsLoader}
            />

            <ProfileRoute
              path="/showcalls"
              load={ShowCallsLoader}
            />

            <ProtectedRoute
              path="/settings"
              load={SettingsLoader}
            />

            <ProfileRoute
              path="/dashboard"
              load={DashboardLoader}
            />

            <ProtectedRoute
              path="/reviews"
              load={ReviewsLoader}
            />

            <NoLoginRoute
              path={['/login', '/login/:oobCode']}
              exact
              load={LoginLoader}
            />

            <NoLoginRoute
              path="/forgot-password"
              load={ForgotPasswordLoader}
            />

            <Route
              path="/checkout"
              load={CheckoutLoader}
            />

            <Route
              path="/shows"
              load={ShowsLoader}
            />

            <Route
              path="/artists"
              load={ArtistsLoader}
            />

            <Redirect
              path="/venues/:venueId"
              to="/hosts/:venueId"
            />

            <Redirect
              path="/venues"
              to="/hosts"
            />

            <Route
              path="/hosts"
              load={HostsLoader}
            />

            <Route
              path="/contact-us"
              exact
              load={ContactUsLoader}
            />

            <Route
              path="/consultation"
              load={ConsultationLoader}
            />

            <AdminRoute
              path="/admin"
              load={AdminLoader}
            />

            <ProfileRoute
              path="/profile"
              load={ProfileLoader}
            />

            <Route
              path="/tickets"
              load={TicketsLoader}
            />

            <Route
              path="/invite/:invitationId"
              load={InviteLoader}
            />

            <Route
              path="/o"
              load={OffersLoader}
            />

            <Route
              path="/signup"
              load={SignupLoader}
            />

            <Route
              path="/terms-of-service"
              load={TermsOfServiceLoader}
            />

            <Route
              path="/privacy-policy"
              load={PrivacyPolicyLoader}
            />

            <Route load={FourOhFourLoader} />
          </Switch>

          <MobileSpacer />
        </Box>

        <Analytics />

        <CookieBanner />

        <EventWatcherContainer />

        <Modal />

        <Snackbar />
      </Box>

      <Footer />

      <BottomAppBar />
    </>
  );
};
